import styled, { css } from 'styled-components';

import { AButton, Button, LinkButton, NavButton } from './Button';
import { Caret } from './Text';
import { Div, Span } from './Generic';
import { Icon } from './Icon';
import SC from './SC';

interface DropdownProps {
  open?: boolean;
  alignH?: 'left' | 'center' | 'right';
  alignV?: 'top' | 'center' | 'bottom';
  position?: 'absolute' | 'relative';
}

const Dropdown = styled(Div)<SC<DropdownProps>>`
  ${({ sc: { open, alignH, alignV, position = 'absolute' } = {}, theme }) => {
    const translateX = alignH === 'center' ? '-50%' : 0;
    const translateY = alignV === 'top' ? '-100%' : (alignV === 'center' ? '-50%' : (alignV === 'bottom' ? '100%' : 0));

    return css`
      position: ${position};
      visibility: hidden;
      opacity: 0;
      transition: all 0.1s ease-in;
      box-shadow: ${theme.shadows.xl};
      background: ${theme.colors.white};
      border-radius: ${theme.borderRadiuses.md}px;
      z-index: 400;
      min-width: 100%;
      margin: none;
      padding: 4px;

      &, a, button {
        font-size: ${13 / 16}rem;
      }

      ${position === 'absolute' && css`
        bottom: 0;
        transform: translateY(100%);

        ${(alignH || alignV) && css`
          transform: translate(${translateX}, ${translateY});
        `}

        ${alignH === 'left' && css`
          left: 0;
        `}

        ${alignH === 'center' && css`
          left: 50%;
        `}

        ${alignH === 'right' && css`
          right: 0;
        `}

        ${alignV === 'top' && css`
          bottom: initial;
          top: 0;
        `}

        ${alignV === 'center' && css`
          bottom: initial;
          top: 50%;
        `}

        ${alignV === 'bottom' && css`
          top: initial;
          bottom: 0;
        `}
      `}

      ${open && css`
        opacity: 1;
        visibility: visible;
      `}

      > * + * {
        margin-top: 1px;
      }

      ${Button}, ${NavButton}, ${LinkButton}, ${AButton} {
        background: none;
        border-radius: none;
        border: none;
        box-shadow: none;
        color: ${theme.colors.gray[700]};
        display: block;
        padding: ${theme.gutter / 4}px ${theme.gutter / 2}px !important;
        text-align: left;
        white-space: nowrap;
        width: 100%;
        height: auto;

        &:hover, &:focus, &:focus-visible {
          background: ${theme.colors.gray[75]};
          border-radius: ${theme.borderRadiuses.sm}px;
          color: ${theme.colors.gray[800]};
          outline: none !important;
        }

        &:disabled {
          opacity: 1 !important;
          color: ${theme.colors.gray[400]} !important;
        }

        ${Icon} {
          margin-right: ${theme.gutter / 3}px;
        }
      }
    `;
  }}
`;

export const DropdownToggle = styled(Button)``;

interface DropdownOptionsContainerProps {
  isDisabled?: boolean;
}

export const DropdownOptionsContainer = styled(Span)<SC<DropdownOptionsContainerProps>>`
  ${({ theme, sc: { isDisabled } = {} }) => css`
    position: relative;
    display: inline-block;

    ${isDisabled && css`
      pointer-events: none;
      opacity: 0.2;
    `}

    > ${DropdownToggle} {
      padding-left: ${theme.gutter / 4}px;
      padding-right: ${theme.gutter / 4}px;

      &:hover {
        background: ${theme.colors.gray[100]};
      }

      ${Caret} {
        margin-left: 0;
      }
    }
  `}
`;

export default Dropdown;
